import Sensor from "./Sensors.js";
import { getGeomag } from "./Geomag.js";

/**
 * Enhanced Compass class with smoothing techniques inspired by Google/Apple Maps Compass:
 * - Circular buffer for averaging multiple readings (reduces noise)
 * - Frame-rate independent animation interpolation
 * - Maximum rotation speed constraint to prevent jumps
 * - Shortest path angle calculation for smooth 0°/360° transitions
 * - Gyroscope validation to reject magnetic interference
 * - Magnetic declination calculation for true heading (using WMM2025)
 * - Accuracy tracking from device sensors
 */
export class Compass extends Sensor {
    constructor(options = {}) {
        super();
        this.northAngle = options.northAngle || 0;
        this._boundHandler = this._handleOrientation.bind(this);
        this._boundMotionHandler = this._handleMotion.bind(this);
        this._boundAnimationFrame = this._animationFrame.bind(this);
        this._usingAbsolute = false;

        // Smoothing configuration
        this._maxRotationSpeed = options.maxRotationSpeed ?? 900; // Max degrees per second for smooth animation
        this._bufferSize = options.bufferSize ?? 1; // Number of readings to average
        this._animationSpeed = options.animationSpeed ?? 1; // Animation interpolation multiplier

        // gyro-driven prediction (to have a nice gesturefeeling) ---
        this._gyroFreshMs = options.gyroFreshMs ?? 120;     // gyro considered valid for this long
        this._correctionTauSlow = options.correctionTauSlow ?? 0.18; // seconds (stable)
        this._correctionTauFast = options.correctionTauFast ?? 0.06; // seconds (when rotating)
        this._useGyroPrediction = options.useGyroPrediction ?? true;

        // Circular buffer for averaging readings
        this._readingsBuffer = [];
        this._bufferIndex = 0;

        // Animation state
        this._currentHeading = null; // Current animated/displayed heading
        this._targetHeading = null;  // Target heading to animate towards
        this._rawHeading = null;     // Last raw sensor reading
        this._lastAnimationTime = 0;
        this._animationFrameId = null;

        // Gyroscope data for validation
        this._gyroscopeAlpha = 0;
        this._lastGyroTime = 0;

        // Magnetic declination (from WMM)
        this._declination = 0;
        this._declinationSet = false;
        this._geomag = getGeomag();

        // GPS position for declination calculation
        this._latitude = null;
        this._longitude = null;
        this._altitude = 0;

        // Whether the raw heading from device is already true heading (iOS) or magnetic (Android)
        this._isTrueHeading = false;

        // Accuracy tracking (in degrees, lower = more accurate)
        // iOS provides webkitCompassAccuracy, for Android we estimate from sensor variance
        this._accuracy = null;
        this._smoothedAccuracy = null; // Smoothed accuracy for stable cone width
        this._accuracyBuffer = [];
        this._accuracyBufferSize = 10;

        this._data = {
            heading: null,           // Smoothed magnetic heading
            trueHeading: null,       // Smoothed true heading (with declination)
            rawHeading: null,        // Raw magnetic heading from sensor
            declination: 0,          // Magnetic declination at current location
            accuracy: null,          // Accuracy in degrees (lower = better)
            alpha: null,
            beta: null,
            gamma: null,
            absolute: false,
            timestamp: 0
        };
    }

    get heading() {
        // Return TRUE heading (magnetic + declination)
        return this._data?.trueHeading ?? this._data?.heading ?? null;
    }

    get magneticHeading() {
        return this._data?.heading ?? null;
    }

    get trueHeading() {
        return this._data?.trueHeading ?? null;
    }

    get rawHeading() {
        return this._data?.rawHeading ?? null;
    }

    get declination() {
        return this._declination;
    }

    get accuracy() {
        return this._data?.accuracy ?? null;
    }

    /**
     * Set GPS location for declination calculation
     * @param {number} lat - Latitude in degrees
     * @param {number} lon - Longitude in degrees
     * @param {number} alt - Altitude in km (default 0)
     */
    setLocation(lat, lon, alt = 0) {
        if (lat === null || lon === null || !isFinite(lat) || !isFinite(lon)) {
            return;
        }

        this._latitude = lat;
        this._longitude = lon;
        this._altitude = alt;

        // Calculate magnetic declination using WMM2025
        try {
            this._declination = this._geomag.getDeclination(lat, lon, alt);
            this._declinationSet = true;
            this._data.declination = this._declination;
            // console.log(`[Compass] Declination set to ${this._declination.toFixed(2)}° for location (${lat.toFixed(4)}, ${lon.toFixed(4)})`);
        } catch (e) {
            console.error(`[Compass] Failed to calculate declination:`, e);
        }
    }

    setNorthAngle(angle) {
        this.northAngle = angle || 0;
    }

    /**
     * Configure smoothing parameters
     */
    configure(config = {}) {
        if (config.maxRotationSpeed !== undefined) {
            this._maxRotationSpeed = config.maxRotationSpeed;
        }
        if (config.bufferSize !== undefined) {
            this._bufferSize = config.bufferSize;
            this._readingsBuffer = [];
            this._bufferIndex = 0;
        }
        if (config.animationSpeed !== undefined) {
            this._animationSpeed = config.animationSpeed;
        }
    }

    async start() {
        if (this._listening) {
            return true;
        }

        try {
            const granted = await this.requestPermission(
                typeof DeviceOrientationEvent !== `undefined` ? DeviceOrientationEvent : null
            );
            if (!granted) {
                return false;
            }

            // Start listening for motion events to get gyroscope data
            if (typeof DeviceMotionEvent !== `undefined`) {
                const motionGranted = await this.requestPermission(DeviceMotionEvent);
                if (motionGranted) {
                    window.addEventListener(`devicemotion`, this._boundMotionHandler, { passive: true });
                }
            }

            if (this.isAndroid) {
                window.addEventListener(`deviceorientationabsolute`, this._boundHandler, { passive: true });
                this._usingAbsolute = true;
            }
            window.addEventListener(`deviceorientation`, this._boundHandler, { passive: true });
            this._listening = true;

            // Start animation loop
            this._lastAnimationTime = performance.now();
            this._animationFrameId = requestAnimationFrame(this._boundAnimationFrame);

            return true;

        } catch (e) {
            console.error(`[Compass] Start error:`, e);
            return false;
        }
    }

    stop() {
        if (!this._listening) {
            return;
        }

        window.removeEventListener(`deviceorientation`, this._boundHandler);
        window.removeEventListener(`devicemotion`, this._boundMotionHandler);

        if (this._usingAbsolute) {
            window.removeEventListener(`deviceorientationabsolute`, this._boundHandler);
            this._usingAbsolute = false;
        }

        if (this._animationFrameId) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }

        this._listening = false;
    }

    /**
     * Handle gyroscope data for motion validation
     */
    _handleMotion(event) {
        const rotationRate = event.rotationRate;
        if (rotationRate && rotationRate.alpha !== null && isFinite(rotationRate.alpha)) {
            this._gyroscopeAlpha = rotationRate.alpha;
            this._lastGyroTime = performance.now();
        }
    }

    _handleOrientation(event) {
        const isAbsolute = event.absolute === true;

        if (this.isAndroid && this._usingAbsolute && !isAbsolute) {
            return;
        }

        let rawHeading = null;
        let isTrueHeading = false;

        if (typeof event.webkitCompassHeading === `number`) {
            rawHeading = event.webkitCompassHeading;
            isTrueHeading = false;

            // iOS also provides accuracy in degrees
            if (typeof event.webkitCompassAccuracy === `number` && event.webkitCompassAccuracy >= 0) {
                this._updateAccuracy(event.webkitCompassAccuracy);
            }
        } else if (event.alpha !== null && isFinite(event.alpha)) {
            // Android/other: calculate MAGNETIC heading from alpha
            rawHeading = this._calculateHeading(event);
            isTrueHeading = false;

            // Android doesn't provide accuracy, so we estimate from sensor variance
            this._estimateAccuracy(rawHeading);
        }

        if (rawHeading === null || !isFinite(rawHeading)) {
            return;
        }

        // Apply north angle offset
        rawHeading = this.normalizeAngle(rawHeading - this.northAngle);
        this._rawHeading = rawHeading;
        this._isTrueHeading = isTrueHeading;

        // Add to circular buffer for averaging
        this._addToBuffer(rawHeading);

        // Calculate averaged target heading
        const averagedHeading = this._getAveragedHeading();

        // Validate against gyroscope to reject magnetic jumps
        if (this._shouldAcceptReading(averagedHeading)) {
            this._setTargetHeading(averagedHeading);
        }

        // Store raw event data
        this._data.rawHeading = rawHeading;
        this._data.alpha = event.alpha;
        this._data.beta = event.beta;
        this._data.gamma = event.gamma;
        this._data.absolute = isAbsolute;
    }

    /**
     * Update accuracy from iOS webkitCompassAccuracy (use smoothed version)
     */
    _updateAccuracy(accuracy) {
        this._updateAccuracySmoothed(accuracy);
    }

    /**
     * Update accuracy from iOS webkitCompassAccuracy with smoothing
     */
    _updateAccuracySmoothed(newAccuracy) {
        if (this._smoothedAccuracy === null) {
            this._smoothedAccuracy = newAccuracy;
        } else {
            // Smooth accuracy changes - use very slow interpolation to avoid jumpy cone
            // Only ~5% weight to new readings for stability
            this._smoothedAccuracy = this._smoothedAccuracy * 0.95 + newAccuracy * 0.05;
        }
        this._accuracy = this._smoothedAccuracy;
        this._data.accuracy = this._smoothedAccuracy;
    }

    /**
     * Estimate accuracy for Android by calculating variance of recent readings
     */
    _estimateAccuracy(heading) {
        // Add to accuracy buffer
        if (this._accuracyBuffer.length >= this._accuracyBufferSize) {
            this._accuracyBuffer.shift();
        }
        this._accuracyBuffer.push(heading);

        if (this._accuracyBuffer.length < 3) {
            // Not enough data yet - use default and smooth it
            this._updateAccuracySmoothed(25);
            return;
        }

        // Calculate circular standard deviation
        let sinSum = 0, cosSum = 0;
        for (const angle of this._accuracyBuffer) {
            const rad = angle * Math.PI / 180;
            sinSum += Math.sin(rad);
            cosSum += Math.cos(rad);
        }
        const n = this._accuracyBuffer.length;
        const R = Math.sqrt(sinSum * sinSum + cosSum * cosSum) / n;

        // Convert R to circular standard deviation (in degrees)
        // R close to 1 = low variance = high accuracy
        // R close to 0 = high variance = low accuracy
        const circularStdDev = Math.sqrt(-2 * Math.log(Math.max(R, 0.01))) * (180 / Math.PI);

        // Clamp to reasonable range (15° to 60°) - narrower range for less dramatic changes
        const clampedAccuracy = Math.max(15, Math.min(60, circularStdDev));

        // Apply smoothing
        this._updateAccuracySmoothed(clampedAccuracy);
    }

    /**
     * Add reading to circular buffer
     */
    _addToBuffer(heading) {
        if (this._readingsBuffer.length < this._bufferSize) {
            this._readingsBuffer.push(heading);
        } else {
            this._readingsBuffer[this._bufferIndex] = heading;
            this._bufferIndex = (this._bufferIndex + 1) % this._bufferSize;
        }
    }

    /**
     * Get averaged heading from buffer using circular mean
     * (properly handles 0°/360° boundary)
     * To avoid wrong average(359°, 1°) = 180°
     */
    _getAveragedHeading() {
        if (this._readingsBuffer.length === 0) {
            return this._rawHeading;
        }

        // Use circular mean for proper angle averaging
        let sinSum = 0;
        let cosSum = 0;

        for (const angle of this._readingsBuffer) {
            const rad = angle * Math.PI / 180;
            sinSum += Math.sin(rad);
            cosSum += Math.cos(rad);
        }

        const avgRad = Math.atan2(sinSum, cosSum);
        return this.normalizeAngle(avgRad * 180 / Math.PI);
    }

    /**
     * Validate if we should accept this reading based on gyroscope
     */
    _shouldAcceptReading(newHeading) {
        if (this._targetHeading === null) {
            return true;
        }

        const delta = Math.abs(this.shortestAngleDelta(this._targetHeading, newHeading));
        const gyroVelocity = Math.abs(this._gyroscopeAlpha);
        const timeSinceGyro = performance.now() - this._lastGyroTime;

        // If gyroscope data is stale (>200ms), accept the reading
        if (timeSinceGyro > 200) {
            return true;
        }

        // If change is large (>30°) but gyroscope shows no rotation (<20°/s)
        // This is likely magnetic interference - reject it
        if (delta > 30 && gyroVelocity < 20) {
            return false;
        }

        return true;
    }

    /**
     * Set target heading using shortest path calculation
     * This ensures smooth transitions across 0°/360° boundary
     */
    _setTargetHeading(heading) {
        heading = this.normalizeAngle(heading);

        if (this._currentHeading === null) {
            // First reading - initialize immediately
            this._currentHeading = heading;
            this._targetHeading = heading;
            this._emitHeading(heading);
            return;
        }

        // Normalize current heading
        this._currentHeading = this.normalizeAngle(this._currentHeading);

        // Find shortest path
        const lowAngle = heading - 360;
        const highAngle = heading + 360;

        const range1 = Math.abs(this._currentHeading - lowAngle);
        const range2 = Math.abs(this._currentHeading - heading);
        const range3 = Math.abs(this._currentHeading - highAngle);

        if (range1 < range2 && range1 < range3) {
            this._targetHeading = lowAngle;
        } else if (range2 <= range1 && range2 <= range3) {
            this._targetHeading = heading;
        } else {
            this._targetHeading = highAngle;
        }
    }

    /**
     * Animation frame for smooth compass updates
     */
    _animationFrame(currentTime) {
        if (!this._listening) {
            return;
        }

        const dt = (currentTime - this._lastAnimationTime) / 1000;
        this._lastAnimationTime = currentTime;

        if (this._currentHeading !== null && this._targetHeading !== null && dt > 0) {
            const now = performance.now();
            const gyroFresh = this._useGyroPrediction && (now - this._lastGyroTime) <= this._gyroFreshMs;

            // 1) Predict using gyro (responsive, no lag)
            if (gyroFresh) {
                this._currentHeading = this.normalizeAngle(this._currentHeading + this._gyroscopeAlpha * dt);
            }

            // 2) Correct toward magnetometer target (prevents drift)
            const delta = this.shortestAngleDelta(this._currentHeading, this._targetHeading);

            // Adaptive correction: when rotating fast, correct faster (feels “snappy”)
            const gyroSpeed = gyroFresh ? Math.abs(this._gyroscopeAlpha) : 0;
            const tau = gyroSpeed > 60 ? this._correctionTauFast : this._correctionTauSlow;

            // Frame-rate independent easing: k = 1 - exp(-dt/tau)
            const k = 1 - Math.exp(-dt / Math.max(0.001, tau));

            // Optional: clamp correction rate if gyro is stale (avoid jumps)
            let step = delta * k;
            if (!gyroFresh) {
                const maxStep = (this._maxRotationSpeed ?? 900) * dt;
                if (Math.abs(step) > maxStep) {
                    step = Math.sign(step) * maxStep;
                }
            }

            this._currentHeading = this.normalizeAngle(this._currentHeading + step);
            this._emitHeading(this._currentHeading);
        }

        this._animationFrameId = requestAnimationFrame(this._boundAnimationFrame);
    }

    /**
     * Emit heading update
     */
    _emitHeading(heading) {
        // heading is the smoothed magnetic heading (or true on iOS)
        this._data.heading = heading;

        // Calculate true heading by adding declination
        this._data.trueHeading = this.normalizeAngle(heading + this._declination);

        this._data.declination = this._declination;
        this._data.timestamp = performance.now();

        this.dispatchEvent(new CustomEvent(`compass`, { detail: this._data }));
    }

    _calculateHeading(event) {
        const { alpha, beta, gamma } = event;

        // When device is relatively flat, use simple calculation
        if (Math.abs(beta) < 10 && Math.abs(gamma) < 10) {
            return this.normalizeAngle(360 - alpha);
        }

        // When device is tilted, compensate for beta and gamma
        // This formula handles device tilt to get true compass heading
        return this.normalizeAngle(-(alpha + beta * gamma / 90));
    }

    /**
     * Force reset the smoothed heading (useful after floor change or teleportation)
     */
    resetHeading(newHeading = null) {
        this._readingsBuffer = [];
        this._bufferIndex = 0;

        if (newHeading !== null) {
            this._currentHeading = this.normalizeAngle(newHeading);
            this._targetHeading = this._currentHeading;
            this._emitHeading(this._currentHeading);
        } else {
            this._currentHeading = null;
            this._targetHeading = null;
        }
    }
}

export default Compass;

